import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Mail, Calendar, Copy, 
  CheckCircle, AlertCircle, XCircle, ExternalLink, Loader
} from 'lucide-react';
import { formatDate, copyToClipboard } from '../utils/helpers';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API = 'https://tradebot-k4nt.onrender.com/api';

export default function Account() {
  const navigate = useNavigate();
  const { user: ctxUser, balance } = useUser();
  
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState('loading');
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    if (!ctxUser?.telegramName) {
      navigate('/login');
      return;
    }
    fetchAccountData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxUser, navigate]);

  const fetchAccountData = async () => {
    setLoading(true);
    try {
      const userRes = await fetch(`${API}/users/${ctxUser.telegramName}`);
      const userData = await userRes.json();
      setDashboardData(userData?.usernameInfo ?? null);

      const stripeRes = await fetch(`${API}/payment/retive-stripe-account-info/web/${ctxUser.telegramName}`);
      const stripeData = await stripeRes.json();
      
      const { allAccountObj } = stripeData;
      const usd = allAccountObj?.retrieveUSD;
      const gbp = allAccountObj?.retrieveGBP;
      const yen = allAccountObj?.retrieveYEN;

      if (usd === 2 && gbp === 2 && yen === 2) {
        setVerificationStatus('not_verified');
      } else {
        const isUsdVerified = usd && usd !== 2 && usd.charges_enabled && usd.payouts_enabled;
        const isGbpVerified = gbp && gbp !== 2 && gbp.charges_enabled && gbp.payouts_enabled;
        const isYenVerified = yen && yen !== 2 && yen.charges_enabled && yen.payouts_enabled;

        if (isUsdVerified || isGbpVerified || isYenVerified) {
          setVerificationStatus('verified');
        } else {
          setVerificationStatus('pending');
        }
      }
    } catch (err) {
      console.error('Account data fetch error:', err);
      toast.error('Could not load account data');
      setVerificationStatus('not_verified');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUsername = async () => {
    const success = await copyToClipboard(ctxUser?.telegramName);
    if (success) toast.success('Username copied!');
  };

  const displayBalance = () => {
    if (!balance) return '$0.00';
    const raw = typeof balance === 'string' ? balance : `$${balance}`;
    const num = parseFloat(raw.replace('$', '').replace(/,/g, ''));
    if (isNaN(num)) return raw;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const STATUS_CONFIG = {
    not_verified: {
      label: 'Not Verified',
      desc: 'Complete account verification to unlock all features',
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200',
      btnText: 'Complete Verification',
      btnColor: 'bg-red-600 hover:bg-red-700',
    },
    pending: {
      label: 'Pending Verification',
      desc: 'Your verification is under review by Stripe',
      icon: AlertCircle,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      btnText: 'View Status',
      btnColor: 'bg-amber-600 hover:bg-amber-700',
    },
    verified: {
      label: 'Verified & Active',
      desc: 'Your account is fully verified and active',
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200',
      btnText: null,
      btnColor: null,
    },
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader className="animate-spin h-12 w-12 text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const statusCfg = STATUS_CONFIG[verificationStatus] || STATUS_CONFIG.not_verified;
  const StatusIcon = statusCfg.icon;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => navigate('/settings')} 
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900 transition">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Settings</span>
          </button>
          <h1 className="text-lg font-semibold text-slate-900">Account Details</h1>
          <div className="w-32"></div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center justify-center mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl flex items-center justify-center text-white text-4xl font-bold shadow-lg">
              {ctxUser.telegramName?.charAt(1)?.toUpperCase() || 'U'}
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <User className="text-slate-400" size={20} />
                  <div>
                    <p className="text-sm text-slate-600">Username</p>
                    <p className="font-semibold text-slate-900">{ctxUser.telegramName}</p>
                  </div>
                </div>
                <button onClick={handleCopyUsername} className="p-2 hover:bg-slate-200 rounded-lg transition">
                  <Copy size={18} className="text-slate-600" />
                </button>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Mail className="text-slate-400" size={20} />
                <div>
                  <p className="text-sm text-slate-600">Email</p>
                  <p className="font-semibold text-slate-900">
                    {dashboardData?.email || ctxUser.email || 'Not provided'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Calendar className="text-slate-400" size={20} />
                <div>
                  <p className="text-sm text-slate-600">Member Since</p>
                  <p className="font-semibold text-slate-900">
                    {formatDate(dashboardData?.dateOfRegistration || ctxUser.dateCreated) || 'Recently'}
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 ${statusCfg.bg} border ${statusCfg.border} rounded-xl`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <StatusIcon className={statusCfg.color} size={20} />
                  <div>
                    <p className="text-sm text-slate-600">Account Status</p>
                    <p className={`font-semibold ${statusCfg.color}`}>{statusCfg.label}</p>
                    <p className="text-xs text-slate-500 mt-1">{statusCfg.desc}</p>
                  </div>
                </div>
                {statusCfg.btnText && (
                  <button
                    onClick={() => navigate('/account-verification')}
                    className={`flex items-center gap-1 px-3 py-1.5 ${statusCfg.btnColor} text-white text-sm font-medium rounded-lg transition shrink-0`}
                  >
                    {statusCfg.btnText}
                    <ExternalLink size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl p-8 text-white">
          <h3 className="text-lg font-bold mb-6">Account Summary</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <span className="text-slate-300">Current Balance</span>
              <span className="text-2xl font-bold">{displayBalance()}</span>
            </div>
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <span className="text-slate-300">Account Type</span>
              <span className="font-semibold">Individual</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-300">Account Number</span>
              <span className="font-mono font-semibold">
                {dashboardData?.accountNumber || '•••• •••• ••••'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <button
              onClick={() => navigate('/deposit')}
              className="py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition"
            >
              Deposit
            </button>
            <button
              onClick={() => navigate('/withdraw')}
              className="py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition"
            >
              Withdraw
            </button>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}
